// Flight, course, and scoring. No rendering. The sea and the boat both read this.
// +x is right. The camera sits on +z and looks toward -z, so +x is screen-right.

export const SPEED = 14.8;
export const START_Z = 8;
export const START_Y = 3.15;
export const LIFT = 4.6;
export const GLIDE = -3.4;
export const DAMP = 3.3;
export const V_UP = 1.55;
export const V_DOWN = -1.25;
export const STRAFE = 12;
export const SIDE_DAMP = 2.55;
export const V_SIDE = 5.2;
export const X_MAX = 7.4;
export const GAP = 26;
export const FIRST_Z = -16;
export const Y_MAX = 8.4;
export const WATER_KISS = 1.18;
export const WATER_PLUNGE = 0.7;

// Soft garden hymn — original. Warm major, ~86 BPM, clear singable line.
// beats drive how far apart the matching ring sits on the course.
export const BPM = 86;
export const BEAT_Z = SPEED * (60 / BPM);

// midi + duration. Phrase breaths get longer rests as wider gaps.
export const SCORE = [
  // Open invitation — gentle rise
  { midi: 60, beats: 1 },
  { midi: 64, beats: 1 },
  { midi: 67, beats: 1.5 },
  { midi: 69, beats: 0.5 },
  { midi: 67, beats: 2 },
  // Hopeful crest
  { midi: 64, beats: 1 },
  { midi: 67, beats: 1 },
  { midi: 72, beats: 1.5 },
  { midi: 74, beats: 0.5 },
  { midi: 72, beats: 2 },
  // Soft turn / wandering mediant color
  { midi: 71, beats: 1 },
  { midi: 69, beats: 1 },
  { midi: 67, beats: 0.75 },
  { midi: 64, beats: 0.75 },
  { midi: 62, beats: 1.5 },
  // Homeward settle
  { midi: 60, beats: 1 },
  { midi: 62, beats: 0.5 },
  { midi: 64, beats: 0.5 },
  { midi: 67, beats: 1 },
  { midi: 64, beats: 1 },
  { midi: 60, beats: 1 },
  { midi: 57, beats: 1 },
  { midi: 60, beats: 2 },
  // Final bloom into the shrine
  { midi: 64, beats: 1 },
  { midi: 67, beats: 1 },
  { midi: 72, beats: 2 },
];

// Back-compat alias used by noteFreq / older call sites.
export const MELODY = SCORE.map((note) => note.midi);

// Lateral / height route shaped to the hymn's contour (not evenly spaced).
const SHAPE = [
  { x: 0, y: 3.25, r: 2.6 },
  { x: 0.15, y: 3.45, r: 2.35 },
  { x: 0.55, y: 3.85, r: 2.1 },
  { x: 1.1, y: 4.15, r: 1.95 },
  { x: 0.4, y: 3.7, r: 1.85 },
  { x: -0.2, y: 3.35, r: 1.72 },
  { x: 0.9, y: 3.95, r: 1.62 },
  { x: 2.4, y: 4.55, r: 1.55 },
  { x: 3.5, y: 4.95, r: 1.48 },
  { x: 2.2, y: 4.35, r: 1.42 },
  { x: 0.6, y: 3.7, r: 1.38 },
  { x: -1.2, y: 3.15, r: 1.34 },
  { x: -2.8, y: 2.85, r: 1.3 },
  { x: -3.6, y: 3.2, r: 1.28 },
  { x: -2.0, y: 3.75, r: 1.26 },
  { x: -0.4, y: 3.35, r: 1.24 },
  { x: 0.8, y: 3.55, r: 1.24 },
  { x: 1.6, y: 3.9, r: 1.22 },
  { x: 2.8, y: 4.35, r: 1.22 },
  { x: 1.4, y: 3.85, r: 1.22 },
  { x: 0.1, y: 3.35, r: 1.24 },
  { x: -1.5, y: 2.95, r: 1.26 },
  { x: 0, y: 3.55, r: 1.4 },
  { x: 1.2, y: 4.05, r: 1.5 },
  { x: 2.4, y: 4.55, r: 1.65 },
  { x: 0.3, y: 4.25, r: 2.05 },
];

export function midiFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function noteFreq(index) {
  const lift = Math.min(Math.floor(index / MELODY.length), 1);
  return midiFreq(MELODY[index % MELODY.length] + lift * 12);
}

export function scoreNote(index) {
  return SCORE[index % SCORE.length];
}

export function hashSeed(text) {
  let h = 2166136261;
  const s = String(text);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(v, a, b) {
  return Math.max(a, Math.min(b, v));
}

function makeRing(index, x, y, r, z, beats = 1) {
  return { index, x, y, r, z, beats, done: false };
}

function shapeAt(index) {
  if (index < SHAPE.length) return SHAPE[index];
  const i = index;
  const amp = Math.min(2.4, 0.7 + (i - SHAPE.length) * 0.045);
  const y =
    3.6 +
    Math.sin(i * 0.62) * amp +
    Math.sin(i * 0.21) * 0.45;
  const x = Math.sin(i * 0.55) * Math.min(4.4, 1.4 + (i - SHAPE.length) * 0.04);
  const r = Math.max(1.18, 1.7 - (i - SHAPE.length) * 0.01);
  return { x, y, r };
}

function gapFor(index) {
  const beats = scoreNote(index).beats;
  // Phrase holds open the sea; pickups sit closer. Keep a playable minimum.
  return Math.max(10, BEAT_Z * beats);
}

export function buildRun(mode, seed) {
  const name = mode === "daily" ? "daily" : "open";
  const rng = mulberry32(hashSeed(seed || name));
  const a1 = rng() * Math.PI * 2;
  const a2 = rng() * Math.PI * 2;
  const rings = [];
  let prevY = SHAPE[0].y;
  let prevX = SHAPE[0].x;
  let z = FIRST_Z;
  for (let i = 0; i < SHAPE.length; i++) {
    let y = SHAPE[i].y;
    let x = SHAPE[i].x;
    if (name === "daily" && i >= 3) {
      const amp = i < 6 ? 0.22 : 0.7;
      const bias =
        Math.sin(i * 0.37 + a1) * amp + Math.sin(i * 0.13 + a2) * amp * 0.45;
      y = SHAPE[i].y + bias;
      y = prevY + clamp(y - prevY, -1.05, 1.05);
      y = clamp(y, 2.15, 6.3);
      x = SHAPE[i].x + Math.sin(i * 0.53 + a2) * (i < 8 ? 0.35 : 0.9);
      x = prevX + clamp(x - prevX, -2.15, 2.15);
      x = clamp(x, -5.8, 5.8);
    }
    const beats = scoreNote(i).beats;
    rings.push(makeRing(i, x, y, SHAPE[i].r, z, beats));
    prevY = y;
    prevX = x;
    z -= gapFor(i);
  }
  const shrineZ = rings[rings.length - 1].z - Math.max(32, BEAT_Z * 3.5);
  return {
    mode: name,
    seed: String(seed || name),
    rings,
    shrineZ,
    shrineDone: false,
    flight: { z: START_Z, x: 0, y: START_Y, vx: 0, vy: 0, speed: SPEED },
    flame: 3,
    score: 0,
    chain: 0,
    bestChain: 0,
    perfects: 0,
    goods: 0,
    skims: 0,
    misses: 0,
    notes: [],
    dead: false,
    frozen: false,
    arrived: false,
    endless: false,
    time: 0,
    plungeCd: 0,
    kissing: false,
  };
}

export function pathX(run, z) {
  const rings = run.rings;
  if (!rings.length) return 0;
  if (z >= rings[0].z) {
    const span = START_Z - rings[0].z;
    const t = span <= 0 ? 1 : (START_Z - z) / span;
    return rings[0].x * clamp(t, 0, 1);
  }
  const last = rings[rings.length - 1];
  if (z <= last.z) return last.x;
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i];
    const b = rings[i + 1];
    if (z <= a.z && z >= b.z) {
      const t = (a.z - z) / (a.z - b.z);
      return a.x + (b.x - a.x) * t;
    }
  }
  return last.x;
}

export function pathY(run, z) {
  const rings = run.rings;
  if (!rings.length) return START_Y;
  if (z >= rings[0].z) {
    const span = START_Z - rings[0].z;
    const t = span <= 0 ? 1 : (START_Z - z) / span;
    return START_Y + (rings[0].y - START_Y) * clamp(t, 0, 1);
  }
  const last = rings[rings.length - 1];
  if (z <= last.z) return last.y;
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i];
    const b = rings[i + 1];
    if (z <= a.z && z >= b.z) {
      const t = (a.z - z) / (a.z - b.z);
      return a.y + (b.y - a.y) * t;
    }
  }
  return last.y;
}

export function gradeAt(ring, y, x = ring.x || 0) {
  const d = Math.hypot(x - (ring.x || 0), y - ring.y);
  if (d > ring.r * 0.96) return "miss";
  if (d <= 0.46) return "perfect";
  if (d <= Math.min(ring.r * 0.46, 0.92)) return "good";
  return "skim";
}

function hurt(run, free) {
  run.chain = 0;
  if (free) return;
  run.flame -= 1;
  if (run.flame <= 0) {
    run.flame = 0;
    run.dead = true;
  }
}

function scoreGrade(run, grade) {
  if (grade === "perfect") {
    run.chain += 1;
    run.perfects += 1;
    if (run.chain > run.bestChain) run.bestChain = run.chain;
    run.score += Math.round(100 * (1 + (run.chain - 1) * 0.18));
    run.flame = Math.min(3, run.flame + 0.35);
  } else if (grade === "good") {
    run.goods += 1;
    run.score += 60;
  } else if (grade === "skim") {
    run.skims += 1;
    run.score += 28;
    run.chain = 0;
  } else {
    run.misses += 1;
  }
}

function pushEndless(run) {
  if (!run.endless) return;
  let last = run.rings[run.rings.length - 1];
  let guard = 0;
  while (last.z > run.flight.z - 240 && guard < 8) {
    const i = last.index + 1;
    const shape = shapeAt(i);
    let y = last.y + clamp(shape.y - last.y, -1.05, 1.05);
    y = clamp(y, 2.05, 6.5);
    let x = last.x + clamp(shape.x - last.x, -2.1, 2.1);
    x = clamp(x, -6.2, 6.2);
    const beats = scoreNote(i).beats;
    const gap = Math.max(14, gapFor(i) - Math.min(4, (i - SHAPE.length) * 0.03));
    const ring = makeRing(i, x, y, shape.r, last.z - gap, beats);
    run.rings.push(ring);
    last = ring;
    guard += 1;
  }
  if (run.rings.length > 70) {
    const keepFrom = run.rings.findIndex((ring) => !ring.done || ring.z < run.flight.z + 40);
    if (keepFrom > 4) run.rings.splice(0, keepFrom);
  }
}

export function stepRun(run, hold, dt, steer = 0) {
  const events = [];
  if (run.dead || run.frozen) return events;
  const step = Math.min(Math.max(dt, 0), 0.05);
  run.time += step;
  run.plungeCd = Math.max(0, run.plungeCd - step);

  const flight = run.flight;
  const side = clamp(steer || 0, -1, 1);
  flight.vx += side * STRAFE * step;
  flight.vx *= Math.exp(-SIDE_DAMP * step);
  flight.vx = clamp(flight.vx, -V_SIDE, V_SIDE);
  flight.x += flight.vx * step;
  if (flight.x > X_MAX || flight.x < -X_MAX) {
    flight.x = clamp(flight.x, -X_MAX, X_MAX);
    flight.vx = 0;
  }

  const accel = hold ? LIFT : GLIDE;
  flight.vy += accel * step;
  flight.vy *= Math.exp(-DAMP * step);
  flight.vy = clamp(flight.vy, V_DOWN, V_UP);
  flight.y += flight.vy * step;

  if (flight.y > Y_MAX) {
    flight.y = Y_MAX;
    flight.vy = Math.min(flight.vy, 0);
  }

  if (flight.y < WATER_KISS && flight.vy < 0) {
    flight.vy *= 0.45;
    if (!run.kissing) {
      run.kissing = true;
      events.push({ type: "kiss", y: flight.y, z: flight.z });
    }
  } else if (flight.y > WATER_KISS + 0.15) {
    run.kissing = false;
  }

  if (flight.y < WATER_PLUNGE && run.plungeCd <= 0) {
    run.plungeCd = 1.15;
    flight.y = WATER_KISS;
    flight.vy = 1.35;
    run.misses += 1;
    hurt(run, false);
    events.push({ type: "plunge", y: flight.y, z: flight.z });
    run.notes.push({ grade: "miss", freq: 110, index: -1 });
    if (run.dead) {
      events.push({ type: "die" });
      return events;
    }
  }

  const prevZ = flight.z;
  if (run.endless) {
    flight.speed = Math.min(17.4, SPEED + Math.max(0, run.time - 48) * 0.025);
  }
  flight.z -= flight.speed * step;
  run.score += flight.speed * step * 0.35;

  for (const ring of run.rings) {
    if (ring.done) continue;
    if (prevZ > ring.z && flight.z <= ring.z) {
      ring.done = true;
      const grade = gradeAt(ring, flight.y, flight.x);
      const freq = noteFreq(ring.index);
      if (grade === "miss") {
        run.misses += 1;
        hurt(run, ring.index < 2);
      } else scoreGrade(run, grade);
      run.notes.push({ grade, freq, index: ring.index });
      events.push({
        type: "ring",
        grade,
        freq,
        index: ring.index,
        x: ring.x,
        y: ring.y,
        z: ring.z,
        r: ring.r,
        free: ring.index < 2 && grade === "miss",
      });
      if (run.dead) {
        events.push({ type: "die" });
        return events;
      }
    }
  }

  if (!run.shrineDone && prevZ > run.shrineZ && flight.z <= run.shrineZ && run.flame > 0) {
    run.shrineDone = true;
    run.arrived = true;
    run.frozen = true;
    run.score += 1000 + run.chain * 50;
    events.push({ type: "arrive" });
  }

  pushEndless(run);
  return events;
}

export function nextRing(run) {
  for (const ring of run.rings) {
    if (!ring.done && ring.z < run.flight.z) return ring;
  }
  return null;
}
